import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';
import { Cell } from '@pelckmans/elementis/components/table';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import { TFunction } from 'i18next';
import { CurrentOrPreviousGroup, Member } from '../../../../../../api/groups';
import { LessonResults, Score, ThemeResults } from '../../../api/results';
import { ScorableItem } from '../../../api/scorable-items';
import { createLinkWithMembersToFilter } from '../../../context/filter-context/hooks/useSelectedMembers';
import { createLinkWithScorableItemToFilter } from '../../../context/filter-context/hooks/useSelectedScorableItems';
import AbsoluteScoreCell from '../../table/cells/AbsoluteScoreCell';
import ActionCell from '../../table/cells/ActionCell';
import DateCell from '../../table/cells/DateCell';
import PercentageCell from '../../table/cells/PercentageCell';
import TimeCell from '../../table/cells/TimeCell';
import { AccumulatedRow, HeaderRow, TableDataGroup, TableDataRow } from '../../types';

type ScorableItemWithAssignment = ScorableItem & { isAssignment: boolean };
type ScoreWithMemberId = Score & { memberId: string };
type AccumulatedScore = Pick<Score, 'score'> & { pending: number; id: string };

const DETAIL_VIEW_COLUMNS = [
  { id: 'head', titleKey: 'PAGES.RESULTS.HEADERS.RESULTS', minWidth: 'xs', maxWidth: 'xs' },
  { id: 'percentage', titleKey: 'PAGES.RESULTS.HEADERS.PERCENTAGE', minWidth: 'xs', maxWidth: 'xs' },
  { id: 'score', titleKey: 'PAGES.RESULTS.HEADERS.SCORE', minWidth: 'sm', maxWidth: 'sm' },
  { id: 'time-spent', titleKey: 'PAGES.RESULTS.HEADERS.TIME_SPENT', minWidth: 'sm', maxWidth: 'sm' },
  { id: 'date-submitted', titleKey: 'PAGES.RESULTS.HEADERS.DATE_SUBMITTED', minWidth: 'sm', maxWidth: 'sm' },
  { id: 'actions', titleKey: 'PAGES.RESULTS.HEADERS.ACTIONS', minWidth: undefined, maxWidth: undefined },
] as const;

export function maskScorableItemName(name: string): string {
  return name.length > 10 ? name.slice(0, 3) + '...' + name.slice(-3) : name;
}

export function createTableHeader(
  t: TFunction,
  usedScorableItems: ScorableItemWithAssignment[],
  language: SupportedUserLanguage,
  group?: CurrentOrPreviousGroup,
  selectedMembers?: Member[],
): HeaderRow {
  return {
    title: t('PAGES.RESULTS.PER_EXERCISE.TABLE_HEADER'),
    columns: usedScorableItems.map(item => ({
      id: item.id,
      title: maskScorableItemName(item.name),
      href: createLinkWithScorableItemToFilter(
        [item.id],
        language as SupportedUserLanguage,
        group?.id,
        selectedMembers,
      ),
      tooltipChildren: (
        <>
          {item.name}
          <Tooltip.Subtext>
            {item.isAssignment
              ? t('PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.TYPE.ASSIGNMENT')
              : t('PAGES.RESULTS.PER_STUDENT.TABLE.SCORABLE_ITEM.TYPE.FREE-EXERCISE')}
          </Tooltip.Subtext>
        </>
      ),
    })),
  };
}

function getMemberScores(lessonData: LessonResults | ThemeResults, memberId: string): Score[] {
  return Object.entries(lessonData.scores).flatMap(([, memberMap]) => memberMap[memberId] || []);
}

function findScoreForItem(memberScores: Score[], scorableItem: ScorableItemWithAssignment): Score | undefined {
  return memberScores.find(
    x => x.exerciseId === scorableItem.id.split('-')[0] && x.isAssignment === scorableItem.isAssignment,
  );
}

function getScoresToAccumulate(
  members: Member[],
  lessonData: LessonResults | ThemeResults,
  scorableItem: ScorableItemWithAssignment,
): ScoreWithMemberId[] {
  return members.flatMap(member => {
    const memberScores = getMemberScores(lessonData, member.id);
    const score = findScoreForItem(memberScores, scorableItem);

    return score ? { ...score, memberId: member.id } : [];
  });
}

export function calculateAccumulatedScore(
  id: string,
  scoresToAccumulate: ScoreWithMemberId[],
): AccumulatedScore | undefined {
  const accumulatedScore = scoresToAccumulate.reduce<AccumulatedScore | null>((acc, curr) => {
    if (!acc && curr) {
      acc = {
        id,
        pending: 0,
        score: { achievable: 0, achieved: 0, maximum: 0 },
      };
    }

    if (curr) {
      if (curr.needsGrading) {
        return {
          ...acc!,
          pending: acc!.pending + curr.score.maximum,
          score: {
            maximum: acc!.score.maximum + curr.score.maximum,
            achievable: acc!.score.achievable,
            achieved: acc!.score.achieved,
          },
        };
      }

      return {
        ...acc!,
        pending: acc!.pending,
        score: {
          maximum: acc!.score.maximum + curr.score.maximum,
          achievable: acc!.score.achievable + curr.score.achievable,
          achieved: acc!.score.achieved + (curr.score.achieved === -1 ? 0 : curr.score.achieved),
        },
      };
    }

    return acc;
  }, null);

  return accumulatedScore || undefined;
}

function createAccumulatedRow(
  group: CurrentOrPreviousGroup,
  members: Member[],
  lessonData: LessonResults | ThemeResults,
  usedScorableItems: ScorableItemWithAssignment[],
): AccumulatedRow {
  return {
    id: group.id,
    title: group.name,
    level: 0,
    columns: usedScorableItems.map(item => {
      const scoresToAccumulate = getScoresToAccumulate(members, lessonData, item);

      if (scoresToAccumulate.length === 0) return undefined;

      return {
        title: `${scoresToAccumulate[0].exerciseId}${scoresToAccumulate[0].memberId}${scoresToAccumulate[0].isAssignment}`,
        ...calculateAccumulatedScore(
          `${scoresToAccumulate[0].exerciseId}${scoresToAccumulate[0].memberId}${scoresToAccumulate[0].isAssignment}`,
          scoresToAccumulate,
        )!,
      };
    }),
  };
}

function createMemberRow(
  group: CurrentOrPreviousGroup,
  member: Member,
  lessonData: LessonResults | ThemeResults,
  usedScorableItems: ScorableItemWithAssignment[],
  language: SupportedUserLanguage,
): TableDataRow | undefined {
  const memberScores = getMemberScores(lessonData, member.id);
  const columns = usedScorableItems.map(item => {
    const score = findScoreForItem(memberScores, item);
    return score
      ? {
          ...score,
          title: item.id,
          pending: 0,
          unachievable: Boolean(score.unachievable),
          memberId: member.id,
        }
      : undefined;
  });

  const memberNoScoreForExercise = columns.every(col => col === undefined);
  if (memberNoScoreForExercise) {
    return undefined;
  }

  return {
    id: `${group.id}-${member.id}`,
    title: `${member.lastName} ${member.firstName}`,
    href: createLinkWithMembersToFilter([`${group.id};${member.id}`], [group.id], language),
    level: 1,
    columns,
  };
}

function createGroupRow(
  group: CurrentOrPreviousGroup,
  members: Member[],
  lessonData: LessonResults | ThemeResults,
  usedScorableItems: ScorableItemWithAssignment[],
  language: SupportedUserLanguage,
): TableDataGroup | undefined {
  const rows = members.reduce<TableDataRow[]>((acc, member) => {
    const row = createMemberRow(group, member, lessonData, usedScorableItems, language);
    if (row) {
      return [...acc, row];
    }
    return acc;
  }, []);

  if (rows.length === 0) {
    return undefined;
  }

  return {
    id: group.id,
    accumulatedRow: createAccumulatedRow(group, members, lessonData, usedScorableItems),
    rows,
  };
}

export function buildTableRows(
  lessonDataPerGroup: Record<string, LessonResults | ThemeResults>,
  displayedGroups: CurrentOrPreviousGroup[],
  members: Record<string, Member[]>,
  usedScorableItems: ScorableItemWithAssignment[],
  language: SupportedUserLanguage,
): TableDataGroup[] {
  return Object.entries(lessonDataPerGroup)
    .map(([groupId, lessonData]) => {
      const group = displayedGroups.find(g => g.id === groupId);
      const groupMembers = members[groupId];

      if (!group || !groupMembers) {
        return undefined;
      }

      return createGroupRow(group, groupMembers, lessonData, usedScorableItems, language);
    })
    .filter((row): row is NonNullable<typeof row> => row !== undefined);
}

export function createTableHeaderForDetailView(firstColumnTitle: string, t: TFunction): HeaderRow {
  return {
    title: firstColumnTitle,
    columns: DETAIL_VIEW_COLUMNS.map(column => ({
      id: column.id,
      title: column.titleKey ? t(column.titleKey) : '',
      minWidth: column.minWidth,
      maxWidth: column.maxWidth,
    })),
  };
}

interface CreateDetailViewRowParams {
  id: string;
  title: string;
  member: Member;
  group: CurrentOrPreviousGroup;
  scorableItem: ScorableItem;
  result: Score;
  level: number;
  subTitleBot?: string;
  subTitleTop?: string;
  tableGroupId?: string;
  icon?: JSX.Element;
  href?: string;
}

export function createDetailViewRow({
  id,
  title,
  tableGroupId,
  level,
  subTitleBot,
  subTitleTop,
  member,
  scorableItem,
  result,
  group,
  icon,
  href,
}: CreateDetailViewRowParams): TableDataRow {
  const subtitle = subTitleBot ? { subTitleBot } : { subTitleTop };

  // cover eduhint test scores of -1
  const realisticScore = { ...result, score: { ...result.score, achieved: Math.max(0, result.score.achieved) } };

  const hideScore = (scorableItem.isAdaptive && result.status !== 'finished') || result.score.achieved === -1;

  return {
    id,
    title,
    level,
    href,
    tableGroupId,
    ...subtitle,
    icon,
    columns: [
      {
        title: 'results',
        ...realisticScore,
        pending: 0,
        memberId: member.id,
        unachievable: Boolean(result.unachievable),
      },
      {
        id: 'percentage',
        title: 'percentage',
        Cell: () => {
          if (hideScore || realisticScore.score.maximum === 0) {
            return <Cell />;
          }

          return (
            <PercentageCell
              achieved={realisticScore?.score.achieved}
              maximum={realisticScore?.score.maximum}
              needsGrading={realisticScore.needsGrading}
            />
          );
        },
      },
      {
        id: 'score',
        title: 'score',
        Cell: () => (
          <AbsoluteScoreCell
            scorableItem={scorableItem}
            score={hideScore ? undefined : realisticScore?.score}
            needsGrading={realisticScore?.needsGrading}
          />
        ),
      },
      {
        id: 'time-spent',
        title: 'time spent',
        Cell: () => <TimeCell duration={realisticScore?.duration || undefined} />,
      },
      {
        id: 'date-submitted',
        title: 'date submitted',
        Cell: () => (
          <DateCell
            completedBySystem={realisticScore?.completedBySystem ?? false}
            isZeroScore={realisticScore?.isZeroScore ?? false}
            achievedAt={realisticScore?.achievedAt}
          />
        ),
      },
      {
        id: 'actions',
        title: 'actions',
        Cell: () => {
          const materialId = scorableItem.id.split('-')[0];
          return (
            <ActionCell
              scorableItem={{ ...scorableItem, id: materialId }}
              memberId={member.id}
              groupId={group.id}
              isAssignment={scorableItem.isAssignment}
              amountOfSessions={result.amountOfSessions}
            />
          );
        },
      },
    ],
  };
}

interface CreateDetailViewAccumulatedRowParams {
  id: string;
  title: string;
  scoresToAccumulate: ScoreWithMemberId[];
  image?: string;
  level?: number;
  horizontalScroll?: boolean;
}

/**
 * Returns the data for the group row in the detail view mode.
 * Details can be found here:
 * https://pelckmans.atlassian.net/wiki/spaces/PAK/pages/4307517453/Ianua+Result+Data+and+Visualization
 */
export function createDetailViewAccumulatedRow({
  id,
  title,
  scoresToAccumulate,
  image,
  level = 0,
  horizontalScroll,
}: CreateDetailViewAccumulatedRowParams): AccumulatedRow {
  const totalScore = scoresToAccumulate.length
    ? calculateAccumulatedScore(
        `${scoresToAccumulate[0].exerciseId}${scoresToAccumulate[0].memberId}${scoresToAccumulate[0].isAssignment}`,
        scoresToAccumulate,
      )
    : null;

  const score = totalScore ? totalScore : { Cell: () => <Cell highlighted /> };

  return {
    id: id,
    title: title,
    level,
    image,
    columns: [
      {
        title: 'results',
        id: 'results-accumulated',
        ...score,
      },
      {
        // https://pelckmans.atlassian.net/wiki/spaces/PAK/pages/4307517453/Ianua+Result+Data+and+Visualization#Aggregate-Score-percentage
        id: 'percentage',
        title: 'percentage',
        Cell: () => {
          return <Cell highlighted minWidth="xs" maxWidth="xs" />;
        },
      },
      {
        // https://pelckmans.atlassian.net/wiki/spaces/PAK/pages/4307517453/Ianua+Result+Data+and+Visualization#Aggregate-Score-points
        id: 'score',
        title: 'score',
        Cell: () => <Cell highlighted minWidth="sm" maxWidth="sm" />,
      },
      {
        // https://pelckmans.atlassian.net/wiki/spaces/PAK/pages/4307517453/Ianua+Result+Data+and+Visualization#Time
        id: 'time-spent',
        title: 'time spent',
        Cell: () => {
          const totalDuration = scoresToAccumulate.reduce((acc, curr) => {
            if (curr.duration) {
              acc += curr.duration;
            }
            return acc;
          }, 0);

          return <TimeCell duration={totalDuration} highlighted />;
        },
      },
      {
        id: 'date-submitted',
        title: 'date submitted',
        Cell: () => <Cell highlighted minWidth="sm" maxWidth="sm" />,
      },
      {
        id: 'actions',
        title: 'actions',
        Cell: () => <Cell highlighted sticky={horizontalScroll} />,
      },
    ],
  };
}

/**
 * Returns the data for the group and its members in the detail view mode.
 */
export function createDetailViewGroupRow(
  group: CurrentOrPreviousGroup,
  members: Member[],
  lessonScores: LessonResults | ThemeResults,
  scorableItem: ScorableItemWithAssignment,
  language: SupportedUserLanguage,
  horizontalScroll: boolean,
): TableDataGroup | undefined {
  const scoresToAccumulate = getScoresToAccumulate(members, lessonScores, scorableItem);
  const rows = members
    .map(member => {
      const memberScores = getMemberScores(lessonScores, member.id);
      const score = findScoreForItem(memberScores, scorableItem);
      if (!score) return null;

      return createDetailViewRow({
        id: member.id,
        title: `${member.lastName} ${member.firstName}`,
        href: createLinkWithMembersToFilter([`${group.id};${member.id}`], [group.id], language),
        level: 1,
        member,
        group,
        scorableItem,
        result: score,
      });
    })
    .filter(Boolean) as TableDataRow[];

  if (rows.length === 0) {
    return undefined;
  }

  return {
    id: group.id,
    accumulatedRow: createDetailViewAccumulatedRow({
      id: group.id,
      title: group.name,
      scoresToAccumulate,
      horizontalScroll,
    }),
    rows,
  };
}

export function buildDetailViewGroupedTableRows(
  lessonScoresPerGroup: Record<string, LessonResults | ThemeResults>,
  displayedGroups: CurrentOrPreviousGroup[],
  members: Record<string, Member[]>,
  scorableItem: ScorableItemWithAssignment,
  language: SupportedUserLanguage,
  horizontalScroll: boolean,
): TableDataGroup[] {
  return Object.entries(lessonScoresPerGroup)
    .map(([groupId, lessonScores]) => {
      const group = displayedGroups.find(g => g.id === groupId);
      const groupMembers = members[groupId];

      if (!group || !groupMembers) {
        return undefined;
      }

      return createDetailViewGroupRow(group, groupMembers, lessonScores, scorableItem, language, horizontalScroll);
    })
    .filter((row): row is NonNullable<typeof row> => row !== undefined);
}
